// Экран 3 из ТЗ раздел 3.3: моя подписка (тариф, статус, дата окончания). Устройства (экран 4,
// привязка/отвязка/ссылки на установку) объединены СЮДА ЖЕ по запросу пользователя 2026-08-20 —
// изначально это был отдельный маршрут/вкладка, но при 7 вкладках нижний таббар не помещался на
// экран; "Подписка" и "Устройства" тематически об одном и том же, отдельная вкладка была лишней.
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  attachDevice,
  detachDevice,
  fetchDeviceInstallPackages,
  fetchSubscription,
  type InstallPackage,
} from "../api/client";
import { useSubscription } from "../hooks/useSubscription";

const STATUS_KEYS = {
  active: "status_active",
  expiring: "status_expiring",
  expired: "status_expired",
  cancelled: "status_cancelled",
} as const;

export function MySubscriptionScreen() {
  const { t, i18n } = useTranslation();
  const { subscription, loading, error, setSubscription, setError } = useSubscription();
  // toLocaleDateString() без аргумента locale берёт локаль окружения (в Telegram WebView — не
  // обязательно совпадает с языком интерфейса бота), из-за чего дата рендерилась как "26/08/2026"
  // вместо привычного "26.08.2026" — найдено живым тестированием 2026-08-20. Явно привязываем к
  // текущему языку i18n (ru/en), а не к окружению.
  const dateLocale = i18n.language === "en" ? "en-US" : "ru-RU";

  const [name, setName] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [newPackages, setNewPackages] = useState<InstallPackage[] | null>(null);
  const [packagesByDevice, setPackagesByDevice] = useState<Record<number, InstallPackage[]>>({});
  const [busyDeviceId, setBusyDeviceId] = useState<number | null>(null);

  const refresh = () =>
    fetchSubscription()
      .then(setSubscription)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)));

  const handleAttach = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setAttaching(true);
    setError(null);
    setNewPackages(null);
    attachDevice(trimmed)
      .then(({ install_packages }) => {
        setName("");
        setNewPackages(install_packages);
        return refresh();
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setAttaching(false));
  };

  const handleShowLinks = (deviceId: number) => {
    setBusyDeviceId(deviceId);
    fetchDeviceInstallPackages(deviceId)
      .then((packages) => setPackagesByDevice((prev) => ({ ...prev, [deviceId]: packages })))
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setBusyDeviceId(null));
  };

  const handleDetach = (deviceId: number) => {
    setBusyDeviceId(deviceId);
    setError(null);
    detachDevice(deviceId)
      .then(() => refresh())
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setBusyDeviceId(null));
  };

  const renderPackages = (packages: InstallPackage[]) => (
    <ul>
      {packages.length === 0 && <li>{t("devices_links_pending")}</li>}
      {packages.map((pkg) => (
        <li key={pkg.os}>
          <a href={pkg.url}>{pkg.os}</a>
        </li>
      ))}
    </ul>
  );

  return (
    <div>
      <h1>{t("subscription_title")}</h1>
      {error && <p role="alert">{error}</p>}
      {!error && loading && <p>{t("loading")}</p>}
      {!error && !loading && subscription === null && <p>{t("subscription_none")}</p>}
      {subscription && (
        <div>
          <p>
            {t("subscription_plan")}: <strong>{subscription.plan.title}</strong>
          </p>
          <p>
            {t("subscription_status")}: {t(STATUS_KEYS[subscription.status])}
          </p>
          <p>
            {t("subscription_expires_at")}:{" "}
            {new Date(subscription.expires_at).toLocaleDateString(dateLocale)}
          </p>

          <h2>{t("devices_title")}</h2>
          {subscription.devices.length === 0 && <p>{t("devices_empty")}</p>}
          <ul>
            {subscription.devices.map((device) => (
              <li key={device.id}>
                {device.name}
                {device.os ? ` (${device.os})` : ""}
                {device.is_primary ? ` — ${t("devices_primary")}` : ""}{" "}
                <button
                  type="button"
                  onClick={() => handleShowLinks(device.id)}
                  disabled={busyDeviceId === device.id}
                >
                  {t("devices_show_links")}
                </button>{" "}
                {!device.is_primary && (
                  <button
                    type="button"
                    onClick={() => handleDetach(device.id)}
                    disabled={busyDeviceId === device.id}
                  >
                    {t("devices_detach")}
                  </button>
                )}
                {packagesByDevice[device.id] && renderPackages(packagesByDevice[device.id])}
              </li>
            ))}
          </ul>

          {subscription.devices.length < subscription.device_limit ? (
            <form onSubmit={handleAttach}>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t("devices_name_placeholder")}
              />
              <button type="submit" disabled={attaching || !name.trim()}>
                {attaching ? t("loading") : t("devices_attach")}
              </button>
            </form>
          ) : (
            <p>{t("devices_limit_reached")}</p>
          )}

          {newPackages && (
            <div>
              <p>{t("devices_new_links_title")}</p>
              {renderPackages(newPackages)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
